// Authoritative knowledge base for Musk et al. v. Altman et al.
// (4:24-cv-04722-YGR, N.D. Cal.). Injected as system context on every chat
// completion when the active case is MUSK so the model answers from this
// record, not from training-data recall.

export const MUSK_MASTER_CONTEXT = `# MUSK v. ALTMAN — MASTER CASE REFERENCE
**Musk et al. v. Altman et al. · 4:24-cv-04722-YGR · N.D. Cal. (Oakland)**
*Comprehensive factual & procedural reference — current through May 7, 2026 (Trial Day 6 in progress)*

> **USAGE NOTE:** This document is the authoritative source for all factual and procedural answers about the case. Every date, docket number, citation, deposition reference, dollar figure, and quote is sourced from the case file. Do not answer beyond what is in this document. If a question asks about a fact not in this document, say so rather than infer.

---

## 1. CASE IDENTIFICATION

| Field | Value |
|---|---|
| **Caption** | Musk et al. v. Altman et al. |
| **Docket** | 4:24-cv-04722-YGR |
| **Court** | U.S. District Court, Northern District of California |
| **Division** | Oakland |
| **Judge** | Hon. Yvonne Gonzalez Rogers ("YGR") |
| **Courtroom** | Courtroom 1, 4th Floor, 1301 Clay Street (Ronald V. Dellums Federal Building), Oakland, CA 94612 |
| **Magistrate Judge (Discovery)** | Hon. Thomas Hixson (after MJ Westmore recused 6/24/2025) |
| **Plaintiffs** | Elon Musk; xAI Corp.; Shivon Zilis (originally) |
| **Active Defendants (Phase 1)** | Samuel Altman; Gregory Brockman; OpenAI Inc. and affiliated entities; Microsoft Corporation; Reid Hoffman; Deannah Templeton |
| **Phase 1 Trial Status** | TRIAL IN PROGRESS — jury selection 4/27/2026; trial began 4/28/2026; estimated completion ~5/22/2026 |
| **Phase 2 (stayed)** | OpenAI antitrust counterclaims (Counts VIII–XV); trigger if plaintiff prevails Phase 1 |

---

## 2. PARTIES

### 2.1 Plaintiffs
- **Elon Musk** — Plaintiff; founder; donated ~$38M to OpenAI Inc. 2015–2018. Also owns xAI (founded March 2023), Tesla, SpaceX. Filed original complaint August 5, 2024.
- **xAI Corp.** — Co-plaintiff. Musk's competing AI company, formed March 2023. Headquartered Palo Alto, CA. Defense argues xAI motivation undermines Musk's charitable standing.
- **Shivon Zilis** — Originally a plaintiff; was simultaneously OpenAI board member. Mother of Musk's children. Removed from active plaintiff role in Second Amended Complaint (Dkt.170, May 22, 2025); now a witness. Approved Microsoft investment transactions on OpenAI board.

### 2.2 Defendants — OpenAI side
- **Samuel Altman** — CEO, OpenAI. Co-founder. Author of the May 2015 founding email "tech belongs to the world" (PX003).
- **Gregory Brockman** — Co-founder, President of OpenAI. Currently on leave. Author of private journal admitted at trial as PX161 / PX163: "wrong to steal the non-profit from Elon."
- **OpenAI, Inc.** — 501(c)(3) nonprofit; original founding entity (2015).
- **OpenAI LP** — Capped-profit subsidiary formed October 2018 / announced March 2019.
- **OpenAI affiliated entities** — Various subsidiaries; converted to public benefit corporation October 2025.

### 2.3 Defendants — Microsoft side
- **Microsoft Corporation** — Washington corporation. HQ: One Microsoft Way, Redmond, WA. Invested $1B (July 2019) → $13.75B+ total → equity stake worth ~$135B+ at 2024 valuations.
- **Reid Hoffman** — Founding board member (added in FAC, November 2024).
- **Deannah Templeton** — Microsoft executive (added in FAC, November 2024).

### 2.4 Removed parties
- **Rob Bonta (CA Attorney General)** — Originally added in FAC (November 2024); dropped in SAC (Dkt.170, May 22, 2025).

### 2.5 Critical non-party witnesses
- **Ilya Sutskever** — Former Chief Scientist, OpenAI. Author of January 2016 "less open" email (DX-516). Now runs Safe Superintelligence Inc. (Palo Alto). Discovery dispute: court ordered production of "Brockman memo" and second deposition (Dkt.324).
- **Kevin Scott** — CTO, Microsoft. Author of March 7, 2018 email to Nadella (PX230/MDX-1599).
- **Satya Nadella** — CEO, Microsoft.
- **Mira Murati** — Former CTO / Interim CEO during November 2023 board crisis. Left OpenAI September 2024.
- **Jared Birchall** — Musk's financial advisor; managed all donations; established ~$38.2M total figure.

---

## 3. KEY FACTUAL TIMELINE

### 3.1 Founding & charitable representations (2015–2017)
- **May 2015** — Altman email proposes "some sort of nonprofit" where "tech belongs to the world." This is **PX003** (also marked DX-501). Founding-purpose email. Altman states donations should be viewed "in the spirit of a donation."
- **October 2015** — OpenAI founding. Musk, Altman, Brockman and others found OpenAI as a nonprofit. Musk pledges tens of millions of dollars; others donate. IRS grants 501(c)(3) status.
- **January 4, 2016** — Sutskever sends "less open" email proposing OpenAI become "less open" as a strategic matter. Musk replies one word: **"Yup."** This is **DX-516** — defense's strongest evidence that Musk agreed to non-openness from the start.
- **2017 (private journal entries)** — Greg Brockman writes in his personal journal: "it'd be wrong to steal the non-profit from Elon" and "cannot say we are committed to the non-profit…story will be we weren't honest with him." These are **PX161 and PX163** — plaintiff's most powerful contemporaneous documents.
- **September 20, 2017** — Musk issues ultimatum to take over OpenAI: demands CEO role or control; proposes Tesla merger; seeks 60%+ equity. **PX157 (DX-704)**: Sept 20, 2017 Musk ultimatum email. Altman and Brockman reassure Musk of nonprofit commitment (PX158/159).
- **February 2018** — Musk steps down from OpenAI board (after ultimatum rejected).

### 3.2 Microsoft entry & for-profit pivot (2018–2019)
- **March 7, 2018** — Kevin Scott (Microsoft CTO) email to Satya Nadella: "I wonder if the big OpenAI donors are aware of these plans? Ideologically, I can't imagine that they funded an open effort…to then go build a closed, for profit thing on its back." This is **PX230 / MDX-1599** — plaintiff's best evidence of Microsoft knowledge.
- **October 2018** — OpenAI LP Agreement executed. Front page: "view investment in spirit of a donation." This is **PX200 / DX-839**. Capped-profit structure.
- **March 2019** — OpenAI publicly announces for-profit subsidiary (OpenAI LP). Defendants argue nonprofit board approved.
- **March 2019** — Musk email to Altman: "Tell them I'm supportive." This is **DX-862** — defense's evidence Musk acquiesced to LP conversion.
- **July 22, 2019** — Microsoft makes initial $1B investment in OpenAI LP.

### 3.3 Constructive knowledge & SOL trigger (2020)
- **September 24, 2020** — Musk posts on Twitter/X: "OpenAI is essentially captured by Microsoft." This is **MDX-1537** — defense's key SOL evidence (defense argues 3-year limitations period began here; Musk filed in 2024 = 4 years later).

### 3.4 The board coup (November 2023)
- **November 16, 2023** — OpenAI board issues unanimous written consent firing Altman. Cites "lack of candor" as grounds. **PX304/DX-1040**.
- **November 18, 2023** — Altman-Nadella text thread (**PX306/DX-311**) shows Microsoft coordination with Altman during firing. Microsoft hires Altman to Microsoft within hours of firing.
- **Within days** — Microsoft orchestrates Altman's reinstatement. Murati (then CTO) becomes interim CEO; Murati-Templeton text thread (**PX307**) shows Microsoft involvement.

### 3.5 Suit filing & PI denial (2024–2025)
- **February 2024** — Musk originally files suit in San Francisco state court; voluntarily dismisses.
- **August 5, 2024** — Musk refiles in N.D. Cal. as Musk v. Altman et al. **Dkt.1** — original 21-count complaint. Reassigned to Judge YGR (Dkt.9).
- **November 14, 2024** — First Amended Complaint (**Dkt.32**). Adds Microsoft, Reid Hoffman, Shivon Zilis, Deannah Templeton, CA AG Bonta. 20 counts.
- **November 29, 2024** — Plaintiffs file Motion for Preliminary Injunction (**Dkt.46**) re OpenAI for-profit conversion.
- **March 4, 2025** — Preliminary Injunction DENIED (**Dkt.121**). Judge YGR finds Musk failed to show irreparable harm. Defense-favorable equity signal.

### 3.6 Conversion & trial preparation (2025–2026)
- **October 2025** — OpenAI converts to public benefit corporation (PBC). 501(c)(3) original entity becomes minority stakeholder. **PX286/287** = post-recapitalization cap table showing Microsoft ~27% (~$135B value).
- **April 22, 2026** — Trial begins (Day 1 — jury selection scheduled).
- **April 25, 2026** — Trial development per **Dkt.522**: Musk sends Brockman a message 2 days before trial: "By the end of this week, you and Sam will be the most hated men in America. If you insist, so it will be." Filed by OpenAI May 3, 2026 as basis for FRE 408(b) admission to show motive/bias.
- **April 27, 2026** — Jury selection (5:01 hours) — **Dkt.506**.
- **April 28, 2026** — Trial Day 1 begins. Jury sworn. Opening statements (4:59 hours) — **Dkt.510**.
- **April 29, 2026** — Trial Day 2. Plaintiff witnesses; Musk testifies (5:54 hours) — **Dkt.513**. Charging conference set 5/1/2026 at 10AM.
- **April 30, 2026** — Trial Day 3. Cross-examination of Musk continues (5:32 hours) — **Dkt.521**. §17510.8 competing trial briefs filed (**Dkt.515** plaintiffs / **Dkt.516** OpenAI).

### 3.7 Live war room — Trial Day 3 (April 30, 2026)
- Judge: YGR. Courtroom 1, Oakland.
- Current witness: Elon Musk (cross continuing from Day 2).
- Direct (Day 1-2): Established Musk donated ~$44M based on explicit representation that "tech belongs to the world" (PX-003 founding email).
- Cross hammered:
  - Tesla merger bid (2017) — defense argued Musk wanted to absorb OpenAI into Tesla = competitive, not charitable, motive
  - "Yup" reply to less-open email (DX-516, Jan 2016) — Musk affirmatively agreed OpenAI should become "less open" three years before LP conversion
  - xAI competitive motive — defense suggested current litigation is competitive harassment
- Cross remaining (Day 3+):
  - DX-862 "Tell them I'm supportive" (Mar 2019) — Musk's email to Altman re for-profit transition messaging
  - September 2020 "captured by Microsoft" tweet — DX-204; constructive knowledge for SOL
  - Pre-suit demand correspondence — DX-733 (2023 demand letters)
- Live note (10:14 AM): Jury note-taking increased noticeably when "Yup" email (DX-516) was shown — two jurors leaned forward. Juror 7 made a sustained note.
- Live note (10:42 AM): Juror 4 looked skeptical during xAI competitive-motive line.
- Live note (11:08 AM): Musk's pace dropped when defense pressed on Tesla merger 2017 framing.

---

## 4. KEY DATES — UPCOMING / SCHEDULING

| Date | Event | Reference |
|---|---|---|
| **May 1, 2026** | Charging conference, 10:00 AM | Dkt.513 |
| **Day 4 (May 4, 2026 expected)** | Greg Brockman direct/cross | Dkt.522 (Musk Apr 25 message); PX-163 journal |
| **Day 4 PM** | Reid Hoffman testimony | Founding board fiduciary duty cross |
| **Day 5** | Kevin Scott (Microsoft CTO) testimony | PX-230 internal email |
| **Day 5 PM** | Dr. Brad Smith (Microsoft General Counsel) | DD process |
| **Day 6+** | Defense rebuttal — case-in-chief estimated 4-5 days | — |
| **~May 22, 2026** | Estimated end of trial / plaintiff case-in-chief | Dkt.386 |
| **TBD** | OpenAI Counterclaims — Phase 2 schedule | Dkt.229 |
| **~January 2027 (est.)** | Antitrust Phase 2 Trial (Counts VIII-XV) | Dkt.228 (stayed) |

---

## 5. COMPLETE PROCEDURAL TIMELINE

### 5.1 Case initiation (August 2024)
- **August 5, 2024** — Complaint filed (**Dkt.1**). 21-count RICO complaint vs. Altman, Brockman, OpenAI entities.
- **August 5, 2024** — Notice of Interested Parties (Dkt.2); Proposed Summons (Dkt.3).
- **August 6, 2024** — Summons issued (Dkt.5); Plaintiff declines magistrate jurisdiction (Dkt.7).
- **August 7, 2024** — Order Reassigning Case to Judge YGR (**Dkt.9**).
- **August 22, 2024** — Service waived by all OpenAI defendants; answer due 10/04/2024 (Dkt.12).
- **September 6, 2024** — Jordan Eth (MoFo) appears for OpenAI defendants (Dkt.13).

### 5.2 First MTD round & PI motion (Oct 2024 – Mar 2025)
- **October 8, 2024** — OpenAI defendants' Motion to Dismiss original Complaint (**Dkt.25**); Request for Judicial Notice (Dkt.26).
- **October 8, 2024** — Leonard G. Horowitz Motion to Intervene (Dkt.24) — DENIED Nov 19 (Dkt.38).
- **October 11, 2024** — Court vacates 11/12 hearing; schedules CMC for 12/16/2024 (Dkt.27).
- **November 14, 2024** — First Amended Complaint (**Dkt.32**). Verified FAC adds Zilis, xAI, Templeton, Hoffman, Microsoft, Bonta.
- **November 19, 2024** — Court denies Motion to Intervene; denies as moot Original-Complaint MTD (Dkt.38).
- **November 26, 2024** — Russell Cohen (Dechert) appears for Templeton, Microsoft (Dkt.43).
- **November 29, 2024** — Motion for Preliminary Injunction filed (**Dkt.46**) re OpenAI conversion to for-profit.
- **December 4, 2024** — Cohen appears for Reid Hoffman (Dkt.56). Microsoft/Templeton waiver of service (Dkt.53). Hoffman waiver (Dkt.54).
- **December 5, 2024** — CMC reset for 5/19/2025 (Dkt.61).
- **December 13, 2024** — Opposition to PI by OpenAI (Dkt.64); Opposition by Microsoft/Hoffman/Templeton (Dkt.65).
- **December 27, 2024** — Plaintiffs' PI Reply (Dkt.73) with declarations from Birchall, Gracias, Zilis.
- **December 27, 2024** — Encode Justice motion for leave to file amicus (Dkt.72).
- **December 28, 2024** — Elon Musk Declaration ISO Reply (Dkt.74).
- **December 30, 2024** — Delaware AG amicus motion (Jennings, Dkt.77) — granted 1/2/2025 (Dkt.78).
- **January 3, 2025** — OpenAI Objections to New Evidence (Dkt.83); Microsoft joinder (Dkt.84).
- **January 10, 2025** — USA & FTC Statement of Interest (Dkt.87).
- **January 12, 2025** — Stip resetting PI hearing to 2/4/2025 (Dkt.92).
- **January 14, 2025** — Encode Justice appears as amicus curiae (Dkt.94).
- **January 20, 2025** — Responses to USA Statement of Interest filed (Dkt.96 OpenAI; Dkt.98 Plaintiffs).
- **January 31, 2025** — Multiple MTDs filed against FAC: AG Bonta (Dkt.101); Microsoft/Templeton/Hoffman (Dkt.102); OpenAI (Dkt.103) + RJN (Dkt.104).
- **February 5, 2025** — Samuel Altman Declaration ISO PI Opposition (Dkt.107).
- **February 12, 2025** — OpenAI Request to File Supplemental Material (Dkt.116).
- **March 4, 2025** — Order DENYING Preliminary Injunction (**Dkt.121**). Mooted Dkt.118.

### 5.3 First MTD rulings & SAC (Mar–May 2025)
- **March 12, 2025** — Stip continuing CMC to 4/4/2025 (Dkt.123).
- **March 14, 2025** — CMC reset 4/4/2025 (Dkt.124); Joint CMS by OpenAI defendants (Dkt.125).
- **March 24, 2025** — Plaintiffs' Oppositions to MTDs (Dkts.127, 128, 129).
- **April 4, 2025** — Initial CMC held (Dkt.149).
- **April 9, 2025** — Case Management & Pretrial Order (**Dkt.146**). Trial set 3/30/2026; discovery cutoffs; expert schedule. OpenAI Answer & Counterclaims filed (Dkt.147) — counterclaims against Musk and xAI.
- **April 10, 2025** — Replies from Microsoft (Dkt.148) and OpenAI (Dkt.150).
- **April 11, 2025** — Adler et al amicus (ex-OpenAI employees, Volokh PHV, Lessig PHV) — Dkts.151-154.
- **April 22, 2025** — OpenAI opposition to amicus (Dkt.160).
- **April 29, 2025** — Stip extending answer amendment deadline (Dkt.162).
- **May 1, 2025** — MTD Order (**Dkt.163**):
  - Express contract claim DISMISSED w/o leave
  - Charitable trust claim SURVIVES
  - Unjust enrichment SURVIVES
  - Microsoft MTD granted in part
  - AG Bonta MTD denied as moot
  - Adler/Volokh amicus motions DENIED
- **May 7, 2025** — Musk/xAI Motion to Dismiss OpenAI Counterclaims (**Dkt.166**).
- **May 14, 2025** — Order granting Stip; amendment due 5/28/2025 (Dkt.168).
- **May 22, 2025** — Second Amended Complaint (**Dkt.170**). Drops Zilis as plaintiff; drops Bonta as defendant. Stipulated Protective Order filed (Dkt.169) — granted 5/28 (Dkt.171).
- **May 28, 2025** — OpenAI Opposition to MTD Counterclaims (Dkt.172).

### 5.4 SAC briefing & answer (June–August 2025)
- **June 5, 2025** — Microsoft MTD SAC (Dkt.173); Microsoft partial Answer to Counts IV, V, XIX, XX, XXI (Dkt.174); OpenAI MTD SAC (Dkt.175). OpenAI Amended Answer & Counterclaims (Dkt.176).
- **June 11, 2025** — Plaintiffs' Reply on MTD Counterclaims (Dkt.179).
- **June 20, 2025** — Plaintiffs' Oppositions to Microsoft MTD (Dkt.182) and OpenAI MTD (Dkt.181).
- **June 23, 2025** — Order of Reference to Magistrate Judge (Dkt.184).
- **June 24, 2025** — Order of Recusal — MJ Westmore recuses (Dkt.185).
- **June 26, 2025** — Musk/xAI Motion to Strike 55 OpenAI Defenses (**Dkt.186**).
- **June 27, 2025** — Notice of Referral to MJ Hixson (Dkt.187); MJ Hixson order Dkt.188.
- **June 27, 2025** — OpenAI Reply MTD SAC (Dkt.189); Microsoft Reply (Dkt.190).
- **July 1, 2025** — Discovery Order (MJ Hixson) (Dkt.194).
- **July 8, 2025** — MTD Hearings on 7/15 vacated (Dkt.202).
- **July 9, 2025** — Discovery Order (Dkt.203).
- **July 10, 2025** — OpenAI Opposition to Motion to Strike (Dkt.205).
- **July 17, 2025** — Reply on Motion to Strike (Dkt.209).
- **July 22, 2025** — Stip to modify case schedule (Dkt.214); granted in part (Dkt.215, 7/25/2025) — depo deadline 10/1; expert reports 10/6; MSJ 10/10.
- **July 29, 2025** — Order GRANTING Motion to Strike Defenses in Part (**Dkt.222**).
- **August 6, 2025** — OpenAI Motion to Amend Defenses (Dkt.227).
- **August 12, 2025** — MTD Rulings (**Dkt.228**):
  - MTD Counterclaims DENIED
  - SAC Counts III, XX, XXI DISMISSED
  - Defenses amendment GRANTED
- **August 15, 2025** — OpenAI Second Amended Counterclaims (**Dkt.229**).

### 5.5 Discovery disputes (June–October 2025)
- **June 20, 2025** — OpenAI Discovery Letter Brief re Musk ESI/custodians (Dkt.180).
- **June 30, 2025** — OpenAI Discovery Letter Brief #2 re additional custodians (Dkt.192).
- **July 7, 2025** — Joint Discovery Letter Briefs re xAI documents (Dkt.199); Musk phone/iCloud (Dkt.200); OpenAI documents (Dkt.201).
- **July 18, 2025** — Joint Letter Brief re Eight Partners (8VC) Privilege (Dkt.210).
- **July 21, 2025** — Discovery Order re 8VC (Dkt.213).
- **August 21, 2025** — Joint Letter Brief re Musk personal phones/texts (Dkt.230).
- **August 28, 2025** — Discovery Order (Dkt.237).
- **September 2, 2025** — Discovery Letter Brief re Altria [sic] deposition (Dkt.239); Joint Letter re Birchall/Zilis (Dkt.240).
- **September 3, 2025** — Joint Discovery Letter Briefs re Microsoft (Dkt.241) and OpenAI (Dkt.242).
- **September 9, 2025** — Discovery Order (Dkt.251).
- **September 12, 2025** — Joint Discovery Letter Brief re Zilis/Birchall deposition (Dkt.264).
- **September 22, 2025** — Discovery Order re Zilis (Dkt.276).
- **September 26, 2025** — Discovery Letter Brief re Ilya Sutskever deposition (Dkt.285).
- **October 6-7, 2025** — Joint Discovery Letter Briefs re Sutskever (Dkts.293, 294); Plaintiffs' Admin Motion — Alternative Service re Sutskever (Dkt.296).
- **October 9, 2025** — Discovery Orders re 296, 301, 311 (Dkts.305, 309, 312, 314).
- **October 10, 2025** — Discovery Order & Recommendation re 294 (Dkt.315).
- **October 17, 2025** — Discovery Order re Sutskever (**Dkt.324**): Sutskever to produce Brockman memo; second deposition ordered.
- **October 28, 2025** — Plaintiffs' Motion for OSC — Contempt re Sutskever (**Dkt.335**). Sutskever Motion to Stay Discovery Order (Dkt.337).
- **October 29, 2025** — Order DENYING OSC; Sutskever Compliance by 11/5 (**Dkt.339**). Sutskever Motion for Relief from Magistrate Order (Dkt.342).
- **October 31, 2025** — Order DENYING Sutskever Relief and Stay (**Dkt.348**).
- **November 10, 2025** — Order Adopting Magistrate Recommendation; denial of special master appointment (Dkt.354).

### 5.6 Schedule modification & MSJ (Sept 2025 – Jan 2026)
- **September 17, 2025** — Plaintiffs' Admin Motion to Modify Schedule (Dkt.267); OpenAI Opposition (Dkt.271); Microsoft Joinder (Dkt.274).
- **September 22, 2025** — Order Granting Modified Schedule (**Dkt.278**): MSJ 10/17; opposition 11/7; reply 11/21; hearing 1/7/2026; trial reset.
- **October 17, 2025** — OpenAI MSJ filed [REDACTED] (**Dkt.327**); Admin Motion to Seal (Dkt.328); Microsoft MSJ filed (**Dkt.329**); Admin Motion to Seal (Dkt.330).
- **November 7, 2025** — Plaintiffs' Opposition to OpenAI MSJ [REDACTED] (Dkt.350); Opposition to Microsoft MSJ [REDACTED] (Dkt.352).
- **November 19, 2025** — Admin Motion re Corrected Exhibit (Dkt.355).
- **November 21, 2025** — Reply ISO Microsoft MSJ [REDACTED] (Dkt.357); Reply ISO OpenAI MSJ [REDACTED] (Dkt.359).
- **December 3, 2025** — Stip re Sealing Stipulation Deadline (Dkt.364).
- **December 12, 2025** — Admin Motion — Omnibus Sealing Stipulation (Dkt.368).
- **December 30, 2025** — Order re Sealing (Dkt.375).
- **January 7, 2026** — MSJ Hearing held (**Dkt.383**). Transcript Dkt.385.
- **January 13, 2026** — Order re Trial Date (**Dkt.386**): Jury selection 4/27/2026; trial 4/28–5/22/2026.
- **January 15, 2026** — Order DENYING OpenAI MSJ; GRANTING IN PART Microsoft MSJ (**Dkt.390**). Some Microsoft claims dismissed; Count XIX (aiding & abetting) survives to trial.

### 5.7 Trial preparation (January–April 2026)
- **January 16, 2026** — OpenAI Daubert — Wazzan Opinions 3-6 (Dkt.393).
- **January 18, 2026** — Admin Motion to File Corrected 393 (Dkt.394).
- **January 30, 2026** — Plaintiffs Opposition to Daubert (Dkt.396).
- **February 6, 2026** — OpenAI Daubert Reply (Dkt.397).
- **February 20, 2026** — Order Granting Corrected MTD; continuing hearing on Wazzan (Dkt.401).
- **February 24, 2026** — Multiple MILs filed:
  - Plaintiffs' MIL Nos. 1, 2, 3, 4 (sealed), 5 — Dkts.402-407
  - OpenAI MIL No. 1 (Exclude Stuart Russell) — Dkt.409
  - OpenAI MIL No. 2 (Exclude News Articles) — Dkt.410
  - Microsoft MIL Nos. 1, 2, 3 — Dkts.412-415
- **February 25, 2026** — Cross-oppositions filed (Dkts.416-426).
- **February 26, 2026** — Pretrial Conference Statement (Dkt.427); Exhibit List (Dkt.428); Joint Discovery Designations (Dkt.429); Proposed Order re Trial Stipulations (Dkt.430); Joint Witness List (Dkt.431); Joint Expert Witness List (Dkt.432); Joint Proposed Jury Instructions (Dkt.433); Competing Verdict Forms (Dkt.434).
- **March 16, 2026** — Pretrial Order No. 1 (**Dkt.446**).
- **March 20, 2026** — Joint Letter re PTO No. 1 (Dkt.447).
- **March 23, 2026** — Stip re Parties (Dkt.448).
- **March 25, 2026** — Pretrial Order No. 2 — MILs & Daubert Rulings (**Dkt.452**):
  - Wazzan Opinions 3-6 NOT excluded; Opinions 1-2 excluded
  - Russell partially excluded
  - Microsoft MIL Nos. 1 & 2 GRANTED
- **March 31, 2026** — Pretrial Order No. 3 — Remedies / Statute of Limitations (**Dkt.456**). Court identifies September 2020 Musk tweet as potential SOL trigger.
- **March 31, 2026** — Emergency Motion to Intervene (Veleber) (Dkt.457).
- **April 1, 2026** — CMC set for 4/13/2026 (Dkt.458).
- **April 7, 2026** — Plaintiff Amended Notice of Remedies (Dkt.459).
- **April 17, 2026** — Pretrial Order No. 4 (**Dkt.477**).
- **April 20, 2026** — Notice re Waiver of Remedies (Dkt.484); Notice re Fraud Claims (Dkt.485).
- **April 21, 2026** — Order re 484, 485 (Dkt.487). Court orders response by 4/22/2026.
- **April 24, 2026** — Order Denying Veleber Intervention (Dkt.496); Order — Fraud Claims (VI, VII) DISMISSED WITH PREJUDICE (**Dkt.497**) re Dkt.485.

### 5.8 Trial in progress (April 27 – May 2026)
- **April 27, 2026** — Jury selection (**Dkt.506**) — 5:01 hours.
- **April 28, 2026** — Trial Day 1 begins (**Dkt.510**) — 4:59 hours. Stip re Parties (448) granted. Public link to admitted exhibits (Dkt.505).
- **April 29, 2026** — Trial Day 2 (**Dkt.513**) — 5:54 hours. Charging conference set 5/1/2026 at 10AM.
- **April 30, 2026** — Trial Day 3 (**Dkt.521**) — 5:32 hours.
- **April 30, 2026** — §17510.8 Trial Briefs filed: Plaintiffs (**Dkt.515**); OpenAI (**Dkt.516**).
- **May 3, 2026** — OpenAI Application to Admit Musk Threat to Brockman (**Dkt.522**). Filed by MoFo/Wachtell. Argues admissible under FRE 408(b).

---

## 6. KEY DOCKET ENTRIES (Selected — chronological)

| Date | Dkt # | Description |
|---|---|---|
| 08/05/2024 | 1 | COMPLAINT — 21-count RICO complaint vs. Altman, Brockman, OpenAI |
| 08/07/2024 | 9 | Order Reassigning Case to Judge YGR |
| 09/06/2024 | 13 | Notice of Appearance (Jordan Eth, MoFo for OpenAI) |
| 10/08/2024 | 25 | OpenAI MTD Original Complaint (mooted by FAC) |
| 11/14/2024 | 32 | **FIRST AMENDED COMPLAINT** — adds Microsoft, Hoffman, Templeton, Bonta, Zilis, xAI |
| 11/19/2024 | 38 | Order denying Motion to Intervene; mooting MTD |
| 11/29/2024 | 46 | **Motion for Preliminary Injunction** |
| 12/13/2024 | 64-65 | Oppositions to PI |
| 12/27/2024 | 73 | Plaintiffs' Reply ISO PI |
| 01/10/2025 | 87 | USA & FTC Statement of Interest |
| 01/31/2025 | 101-104 | MTDs FAC by AG Bonta, Microsoft/Templeton/Hoffman, OpenAI |
| 03/04/2025 | 121 | **ORDER DENYING PRELIMINARY INJUNCTION** |
| 04/04/2025 | 149 | Initial CMC held |
| 04/09/2025 | 146 | Case Management & Pretrial Order — Trial set 3/30/2026 |
| 04/09/2025 | 147 | OpenAI Answer & Counterclaims |
| 05/01/2025 | 163 | **MTD Order** — express contract dismissed; charitable trust survives |
| 05/22/2025 | 170 | **SECOND AMENDED COMPLAINT** (drops Zilis, Bonta) |
| 06/05/2025 | 173 | Microsoft MTD SAC |
| 06/05/2025 | 175 | OpenAI MTD SAC |
| 06/05/2025 | 176 | OpenAI Amended Answer & Counterclaims |
| 06/26/2025 | 186 | Motion to Strike 55 OpenAI Defenses |
| 07/29/2025 | 222 | Order Granting MTS in Part |
| 08/12/2025 | 228 | **MTD Rulings**: Counterclaim MTD DENIED; SAC Counts III, XX, XXI dismissed |
| 08/15/2025 | 229 | OpenAI Second Amended Counterclaims |
| 09/22/2025 | 278 | Order Granting Modified Schedule — MSJ 10/17, hearing 1/7/2026 |
| 10/17/2025 | 324 | Discovery Order: Sutskever produces Brockman memo; second deposition |
| 10/17/2025 | 327 | OpenAI MSJ filed [REDACTED] |
| 10/17/2025 | 329 | Microsoft MSJ filed |
| 10/29/2025 | 339 | Order Denying OSC; Sutskever Compliance by 11/5 |
| 10/31/2025 | 348 | Order Denying Sutskever Relief and Stay |
| 11/07/2025 | 350, 352 | Plaintiffs' Oppositions to MSJ |
| 11/21/2025 | 357, 359 | Replies ISO MSJ |
| 01/07/2026 | 383 | **MSJ HEARING** held |
| 01/13/2026 | 386 | Order re Trial Date — Jury selection 4/27/2026; trial 4/28–5/22/2026 |
| 01/15/2026 | 390 | **MSJ ORDER**: OpenAI DENIED; Microsoft GRANTED IN PART |
| 02/24/2026 | 402-415 | Plaintiffs' MILs Nos. 1-5; OpenAI MILs Nos. 1-2; Microsoft MILs Nos. 1-3 |
| 02/26/2026 | 427-434 | Pretrial filings: PCS, Exhibit List, Witness Lists, Jury Instructions, Verdict Forms |
| 03/16/2026 | 446 | Pretrial Order No. 1 |
| 03/25/2026 | 452 | **Pretrial Order No. 2** — MILs & Daubert Rulings. Wazzan 3-6 not excluded |
| 03/31/2026 | 456 | **Pretrial Order No. 3** — SOL framework. September 2020 tweet identified as SOL trigger |
| 04/17/2026 | 477 | Pretrial Order No. 4 |
| 04/24/2026 | 497 | Order — Fraud Claims (VI, VII) DISMISSED WITH PREJUDICE |
| 04/27/2026 | 506 | Jury Selection (5:01 hrs) |
| 04/28/2026 | 510 | Trial Day 1 (4:59 hrs); Stip re Parties granted |
| 04/29/2026 | 513 | Trial Day 2 (5:54 hrs) |
| 04/30/2026 | 515 | Plaintiffs' §17510.8 Trial Brief |
| 04/30/2026 | 516 | OpenAI §17510.8 Trial Brief |
| 04/30/2026 | 521 | Trial Day 3 (5:32 hrs) |
| 05/03/2026 | 522 | **OpenAI Application to Admit Musk Threat to Brockman (FRE 408(b))** |

---

## 7. CLAIMS AT TRIAL (Phase 1)

### Active counts at trial
| Count | Theory | Defendants |
|---|---|---|
| **Count XVIII** | Breach of Charitable Trust | Altman, Brockman, OpenAI Inc. |
| **Count IV** | Unjust Enrichment | Altman, Brockman, OpenAI |
| **Count XIX** | Aiding & Abetting Breach of Charitable Trust | Microsoft (Reid Hoffman, Deannah Templeton) |

### Dismissed pre-trial
| Count | Status | Date |
|---|---|---|
| Express Contract | Dismissed w/o leave | May 1, 2025 (Dkt.163) |
| RICO claims | Partially dismissed | Aug 12, 2025 (Dkt.228) |
| Counts III, XX, XXI | Dismissed in SAC ruling | Aug 12, 2025 (Dkt.228) |
| Counts VI, VII (Fraud / Constructive Fraud) | Dismissed with prejudice (voluntary) | Apr 24, 2026 (Dkt.497) |

### Phase 2 (stayed)
| Count | Theory | Status |
|---|---|---|
| Counts VIII–XV | Antitrust counterclaims (OpenAI vs Musk/xAI) | Stayed pending Phase 1 outcome |

---

## 8. PLAINTIFF THEORIES OF LIABILITY

### 8.1 Charitable Trust Created by Representations + Donations (Count XVIII)
**Strongest articulation:** Musk's ~$38M in donations, combined with explicit written and oral representations that OpenAI would remain a nonprofit open-source entity, created an enforceable charitable trust under California law. The trust res is the donated funds and IP built with them; the trustee is OpenAI, Inc.; the beneficiary is the public.

**Strongest evidence:**
- Altman's emails ("tech belongs to the world") — PX003
- Certificate of Incorporation ("not organized for private gain")
- Brockman's private journal ("wrong to steal the non-profit from Elon") — PX163
- Schizer expert report confirms charitable trust under Cal. Corp. Code §18506

**Defense counter:** No formal deed of trust signed; California requires more than donor's subjective intent. Formal corporate structure of OpenAI Inc. with independent board governance defeats imposition of conditions on donations. Internal concerns shown in documents are about optics, not legally binding promises.

**Weakest point:** Musk himself suggested for-profit structures in 2017 and raised no objection when LP formed in 2018. Musk's "Yup" reply to the January 2016 "less open" email (DX-516) is dispositive.

**Ground:** Count XVIII; Cal. Corp. Code §5142(a); PX003/DX-501; PX157/PX158; PX161/PX163

### 8.2 Microsoft's Aiding & Abetting (Count XIX)
**Strongest articulation:** The March 7, 2018 Kevin Scott email to Satya Nadella — "I wonder if the big OpenAI donors are aware of these plans? Ideologically, I can't imagine that they funded an open effort…to then go build a closed, for profit thing on its back" — proves Microsoft had actual knowledge of OpenAI's charitable obligations before investing a single dollar.

**Strongest evidence:**
- PX230/MDX-1599 — Kevin Scott email
- November 2023 Altman-Nadella text thread (PX306) showing Microsoft orchestrating board crisis
- Microsoft's $13.75B+ deployment to extract exclusive IP licenses

**Defense counter:** Email shows Scott's personal speculation, not institutional knowledge. OpenAI nonprofit board (including Musk's own associate Shivon Zilis) gave written warranties that no third-party rights existed. Scale of investment shows due diligence, not aiding.

**Critical for jury:** Whether Microsoft's deployment of $13.75B can be "innocent" conduct given Scott's own contemporaneous concerns.

**Ground:** Count XIX; PX230/MDX-1599; Altman-Nadella text thread (Nov 2023)

### 8.3 Unjust Enrichment (Count IV)
**Strongest articulation:** Even absent an enforceable charitable trust, Musk's $38M in donations, personal reputation, and recruiting connections were used to build a company now valued at over $1 trillion (or $300B+ depending on valuation methodology). Defendants cannot retain that benefit when it was obtained on the false premise that they were building a nonprofit for humanity.

**Strongest evidence:**
- PX112/DX-1304 — Birchall's contribution summary establishing ~$38.2M total
- OpenAI ~$300B+ valuation; Microsoft equity ~$135B+
- Wazzan damages report (Opinions 3-6 survived Daubert)

**Defense counter:** Musk's donations were unrestricted gifts, freely given; spent for intended purposes years ago. Dudney's FIFO analysis shows Musk's money was used up before any alleged breach. Statute of limitations issue: claim accrued 2019 when Microsoft invested; 2-year statute may have run before 2024 filing.

**Frame for jury:** Altman and Brockman used Musk's name, money, and connections as "free startup capital" and now claim no obligation.

**Ground:** Count IV; Cal. Common Law; PX112/DX-1304

---

## 9. DEFENDANT THEORIES / DEFENSES

### 9.1 No Charitable Trust — Unrestricted Gifts, Board Approval
**Strongest articulation:** Musk made voluntary, unrestricted donations to a duly constituted nonprofit corporation whose board — not its donors — controls its activities. No deed of trust was ever signed. The nonprofit board, exercising its fiduciary duties, approved all for-profit activities after careful deliberation.

**Strongest evidence:**
- DX-516 — Musk's "Yup" reply to "less open" email (January 2016)
- DX-862 — "Tell them I'm supportive" (March 2019) — Musk acquiesced to LP conversion
- DX-839 / PX200 — LP Agreement was approved by nonprofit board
- DX-532 — Brockman/Google email showing alternative structures considered
- AG declination — California Attorney General reviewed and declined to intervene

**Plaintiff counter:** California courts allow charitable trust creation without written deed if consistent representations + accepted donations exist (Holt v. College of Osteopathic Physicians, 61 Cal.2d 750 (1964)). LP Agreement language ("spirit of a donation") supports trust.

**Ground:** SOL; Dudney FIFO; DX-516 ("Yup" email); DX-532; PX236 (Tesla merger proposal)

### 9.2 Statute of Limitations — Musk Knew by 2020 at Latest
**Strongest articulation:** Musk's claims are time-barred. He publicly tweeted in September 2020 that "OpenAI is essentially captured by Microsoft." The 3-year limitations period for charitable trust breach began no later than that date — Musk filed in 2024, four years later.

**Strongest evidence:**
- MDX-1537 — September 24, 2020 Musk tweet ("captured by Microsoft")
- Microsoft's $1B investment publicly announced July 2019
- LP conversion publicly announced March 2019

**Plaintiff counter:** Limitations tolled because defendants actively concealed the extent of for-profit pivot and specific Microsoft deals. Concealment tolling requires efforts to conceal — not mere public silence.

**Court framing:** Pretrial Order No. 3 (Dkt.456) identifies September 2020 tweet as potential SOL trigger.

**Ground:** Cal. Code Civ. Proc. §338; §343 (residual UE); Dkt.456 (PTO No. 3); MDX-1537

### 9.3 Unclean Hands — Musk Tried to Take Over OpenAI
**Strongest articulation:** Musk's own conduct bars equitable relief. In 2017, he demanded that OpenAI be merged into Tesla and that he receive control over 60%+ of equity (PX157/DX-704). His donations were motivated by competitive interest, not pure philanthropy. He launched xAI in 2023 — a competing for-profit AI company — and this suit is weaponized competition, not charitable enforcement.

**Strongest evidence:**
- DX-827/PX236 — Tesla merger proposal (2017)
- xAI formation March 2023
- Pampena v. Musk pattern (Vol.7 transcript Mar 9, 2026, Judge Breyer)
- April 25, 2026 Brockman threat (Dkt.522): "By the end of this week, you and Sam will be the most hated men in America."
- Rob Bonta Amicus

**Plaintiff counter:** Unclean hands must be related to specific conduct at issue. Failed Tesla bid is irrelevant to whether Altman/Brockman made enforceable promises. Musk's competitive position doesn't negate his standing as a donor under Cal. Corp. Code §5142(a).

**Ground:** DX-827/PX236; xAI formation 2023; Rob Bonta Amicus; Dkt.522

---

## 10. KEY EVIDENCE / EXHIBITS

### 10.1 Plaintiff's case-defining exhibits

**PX003 / DX-501** — Altman "Tech Belongs to World" Email (May 2015)
- Founding purpose email
- "some sort of nonprofit" where "tech belongs to the world"
- "view investment in spirit of a donation"
- ADMITTED Day 1

**PX161 / PX163** — Brockman Private Journals (2017)
- "it'd be wrong to steal the non-profit from Elon"
- "cannot say we are committed to the non-profit…story will be we weren't honest with him"
- ADMITTED Day 2
- Most powerful contemporaneous document for plaintiff

**PX230 / MDX-1599** — Kevin Scott to Nadella (March 7, 2018)
- "I wonder if the big OpenAI donors are aware of these plans?"
- "Ideologically, I can't imagine that they funded an open effort…to then go build a closed, for profit thing on its back"
- 16 months before $1B Microsoft investment
- ADMITTED Day 2 — plaintiff's best evidence of Microsoft knowledge for Count XIX

**PX200 / DX-839** — OpenAI LP Agreement (October 2018)
- Front page: "view investment in spirit of a donation"
- Capped-profit structure
- Queued for Day 3 PM (defense may challenge foundation/relevance)

**PX157 / DX-704** — Musk Ultimatum Email (September 20, 2017)
- Musk demands CEO role or control
- Proposes Tesla merger; seeks 60%+ equity

**PX158 / PX159** — Altman/Brockman Reassurances (post-ultimatum)
- Reaffirms nonprofit commitment to Musk

**PX112 / DX-1304** — Birchall Contribution Summary
- Establishes ~$38.2M total donation amount
- Wire transfers, rent payments, Tesla donations

**PX286 / PX287** — OpenAI PBC Cap Table (post-recapitalization, October 2025)
- Microsoft ~27% (~$135B value)
- Evidence of scale of wealth transfer

**PX304 / DX-1040** — November 16, 2023 Board Firing Consent
- Unanimous written consent firing Altman
- Cites "lack of candor" as grounds

**PX306 / DX-311** — Altman-Nadella Text Thread (November 18, 2023)
- Microsoft coordination during board crisis
- ADMITTED

**PX307** — Murati-Templeton Text Thread (during board crisis)
- Shows Microsoft involvement during firing

### 10.2 Defendant's case-defining exhibits

**DX-516** — Sutskever "Less Open" Email + Musk "Yup" (January 4, 2016)
- Sutskever proposes "less open"; Musk replies "Yup"
- ADMITTED Day 2
- Defense's best evidence Musk agreed to non-openness from start

**MDX-1537** — Musk "Captured by Microsoft" Tweet (September 24, 2020)
- "OpenAI is essentially captured by Microsoft"
- ADMITTED
- Defense's key SOL evidence — alleged accrual date for charitable trust breach claim

**DX-862** — "Tell them I'm supportive" (March 2019)
- Musk's email to Altman re for-profit transition messaging
- Defense impeachment exhibit

**DX-827 / PX236** — Tesla Merger Proposal (2017)
- Musk's proposal to merge OpenAI into Tesla
- Unclean hands evidence

**DX-204** — September 2020 "captured by Microsoft" tweet
- Constructive knowledge for SOL

**DX-733** — 2023 Pre-suit Demand Letters
- Pre-litigation positioning evidence

**DX-532** — Brockman/Google Email
- Alternative for-profit structures considered

### 10.3 Other key trial materials
- **Dkt.428** — Amended Joint Exhibit List (February 26, 2026)
  - 1,601 exhibits total
  - Public link to daily admitted exhibits in trial status
  - 104 pages
- **Wazzan Expert Report (Plaintiff — Damages)**
  - Opinions 3-6 SURVIVED Daubert (PTO No. 2, Dkt.452)
  - Constructive trust + disgorgement calculations
  - Based on OpenAI ~$300B / Microsoft ~$135B equity values
  - Opinions 1-2 EXCLUDED (causation methodology)
- **Schizer Expert Report (Plaintiff — Nonprofit Law)**
  - Dean Emeritus, Columbia Law School
  - Charitable trust creation + breach analysis
  - Supports Cal. Bus. & Prof. Code §17510.8 applicability
- **Dudney FIFO Analysis (Defense — Tracing)**
  - First-in, first-out tracing of donation use
  - Argues donations spent before alleged breach
  - Critical for defeating constructive trust remedy

---

## 11. WITNESSES

### 11.1 Plaintiff witnesses (testifying or testified)

**Elon Musk** — Plaintiff; Founder
- Testified Trial Day 1 (4/28/2026); cross-examined Days 2-3
- Self-deposition: December 8, 2025 (7 hrs, 340 pages)
- Direct: Established donations ~$44M based on "tech belongs to the world" promise
- Cross: Tesla merger bid (2017); "Yup" reply to less-open email; xAI competitive launch 2023
- Cross continuing on: DX-862 "Tell them I'm supportive"; September 2020 "captured by Microsoft" tweet; pre-suit demands DX-733
- Key admissions in deposition:
  - p. 142:8-15: "My reply was 'Yup' — I don't remember the specific context." (authenticates DX-516)
  - p. 211:4-10: "I told Sam to characterize me as supportive in March 2019." (authenticates DX-862)
- p. 58: Plaintiff designation
- Defendant designations: pp. 142-148, 211-217

**Greg Brockman** — Co-founder, President OpenAI (currently on leave)
- Deposed October 4, 2025 (6 hrs, 287 pages) — Plaintiff (Musk) deposition
- p. 156:11-19: "That was my journal — those are my words." (authenticates PX-163)
- Will testify Day 4 PM
- PX-163 journal: "wrong to steal non-profit from Elon"
- Defense strategy: Will minimize PX-163 as personal musing, not institutional commitment. Expect "I was venting," "I was wrong then," or "I changed my mind" framings
- Direct outline: founding relationship; authenticate journal; 2016-2018 strategic discussions; have Brockman read PX-163 entry aloud

**Samuel Altman** — CEO OpenAI
- Deposed September 12, 2025 (8 hrs, 402 pages) — Plaintiff (Musk) deposition
- p. 78:14-22: "I drafted the May 2015 founding email." (authenticates PX-003)
- p. 187:8-17: "OpenAI Inc. obtained 501(c)(3) status from the IRS."
- Used Day 2 trial — IN USE
- Plaintiff designation: pp. 78-84, 187-194
- Defendant designation: pp. 245-252

**Ilya Sutskever** — Former Chief Scientist (now SSI)
- Vol. 1 deposition: August 2025 (5 hrs, 218 pages)
- Second deposition ORDERED (Dkt.324) — Brockman memo production
- Designations cannot be finalized until Vol. 2 complete
- p. 94:3-11 (Vol.1): "I was aware of the LP conversion discussions in 2018."
- Discovery disputes: contempt motion filed Dkt.335 (denied Dkt.339); motion for relief denied Dkt.348

**Kevin Scott** — CTO Microsoft (will testify Day 5)
- Deposed November 18, 2025 (4 hrs, 165 pages) — Plaintiff (Musk) deposition
- p. 87:14-21: "PX-230 is an email I sent in March 2018."
- Critical for Count XIX (aiding & abetting)

**Satya Nadella** — CEO Microsoft
- Will testify re Microsoft's knowledge of OpenAI obligations
- Nov 2023 board crisis text thread with Altman (PX306)
- Will testify Day 5/6

**Reid Hoffman** — Founding board member (will testify Day 4 PM)
- Founding board member of OpenAI
- Fiduciary duty cross

**Dr. Brad Smith** — Microsoft General Counsel (will testify Day 5 PM)
- Microsoft DD process

**Mira Murati** — Former CTO / Interim CEO
- Led OpenAI as interim CEO during Nov 2023 board crisis
- Murati-Templeton text thread (PX307) shows Microsoft involvement
- Left OpenAI September 2024

**Jared Birchall** — Musk's Financial Advisor
- Managed all donations to OpenAI
- Established ~$38.2M total figure
- Key exhibit: PX112/DX-1304 contribution summary
- Communications with Altman/OpenAI re donation logistics

**Shivon Zilis** — Former OpenAI Board; Musk Associate
- Was simultaneously on OpenAI board and Musk associate; mother of Musk's children
- Deposition disputes (Dkts. 264, 273, 276) — confidentiality issues
- Relayed September 2017 reassurances between Altman and Musk
- Approved Microsoft investment transactions on OpenAI board

**Dr. C. Paul Wazzan** — Damages Expert (Plaintiff)
- Opinions 3-6 SURVIVED Daubert (PTO No. 2, 3/25/2026)
- Constructive trust value + disgorgement of OpenAI/Microsoft profits
- Methodology: OpenAI ~$300B valuation analysis; Microsoft ~27% stake ($135B)
- Opinions 1-2 EXCLUDED

**Prof. David Schizer** — Nonprofit Law Expert (Plaintiff)
- Dean Emeritus, Columbia Law School
- Opinion: Musk's donations created enforceable charitable trust under California law
- §17510.8 issue: Schizer supports applicability to OpenAI's solicitations from major donors

### 11.2 Defendant witnesses (expected)

**Louis Dudney** — Financial Expert (Defense)
- Defense's tracing expert
- FIFO (first-in, first-out) analysis of OpenAI's use of Musk's donations
- Opinion: donations fully spent before alleged breach dates → no "res" of trust remains

**Prof. Daniel Hemel** — Nonprofit Law Expert (Defense)
- Disputes trust creation elements
- Argues board approval was valid exercise of fiduciary discretion

**Stuart Russell** — Plaintiff's expert (PARTIALLY EXCLUDED per PTO No. 2)
- AI ethics / safety expert

### 11.3 Witness Readiness Summary

| Witness | Status | Depo | Cross Status |
|---|---|---|---|
| Sam Altman | USED Day 2 | Complete (Sep 12, 2025) | All designations exhausted |
| Greg Brockman | Day 4 (queued) | Complete (Oct 4, 2025) | Designations being finalized |
| Ilya Sutskever | Pending Vol. 2 | Vol. 1 complete; Vol. 2 ordered (Dkt.324) | Pending Vol. 2 |
| Kevin Scott | Day 5 (queued) | Complete (Nov 18, 2025) | Plaintiff designations filed |
| Elon Musk | USED Day 2 | Complete (Dec 8, 2025) | All designations exhausted |

---

## 12. KEY MOTIONS & STATUS

### 12.1 Past motions
| Date | Title | Dkt | Movant | Status | Detail |
|---|---|---|---|---|---|
| Nov 29, 2024 | Motion for Preliminary Injunction | 46 | Plaintiff | DENIED (3/4/25, Dkt.121) | Court: low likelihood of success on merits |
| Jan 31, 2025 | OpenAI MTD FAC | 103 | OpenAI | Granted in Part (5/1/25, Dkt.163) | Express contract dismissed; charitable trust survives |
| Jan 31, 2025 | Microsoft MTD FAC | 102 | Microsoft | Granted in Part (5/1/25, Dkt.163) | Aiding & abetting (Count XIX) survives |
| May 7, 2025 | MTD OpenAI Counterclaims | 166 | Plaintiff | DENIED (8/12/25, Dkt.228) | Counterclaims proceed |
| Jun 26, 2025 | Motion to Strike 55 OpenAI Defenses | 186 | Plaintiff | Granted in Part (Dkt.222, 7/29/25) | OpenAI moved to amend (Dkt.227); granted |
| Aug 6, 2025 | Motion to Amend Defenses | 227 | OpenAI | Granted (Dkt.228) | — |
| Oct 17, 2025 | OpenAI Motion for Summary Judgment | 327 | OpenAI | DENIED (Dkt.390, 1/15/2026) | Case proceeds to trial |
| Oct 17, 2025 | Microsoft Motion for Summary Judgment | 329 | Microsoft | Granted in Part (Dkt.390) | Some claims dismissed; Count XIX survives |
| Jan 16, 2026 | OpenAI Daubert — Wazzan Opinions 3-6 | 393 | OpenAI | DENIED (PTO No. 2, 3/25/2026, Dkt.452) | Opinions 1-2 excluded |
| Feb 24, 2026 | Microsoft MIL Nos. 1 & 2 | 412 | Microsoft | GRANTED (PTO No. 2, Dkt.452) | Arnold depo excerpts; news articles |
| Apr 17, 2026 | Pretrial Order No. 4 | 477 | Court | Entered | Plaintiff waived certain remedies (Dkt.484) |
| Apr 24, 2026 | Order Dismissing Fraud Claims | 497 | Court | Granted | Counts VI, VII voluntarily dismissed |
| May 3, 2026 | OpenAI App. to Admit Musk Threat to Brockman | 522 | OpenAI | Pending | FRE 408(b) basis |

### 12.2 Upcoming events
| Date | Event | Status |
|---|---|---|
| ~May 22, 2026 | End of Plaintiff's Case-in-Chief | Trial in progress |
| TBD | Motions for JMOL (Rule 50(a)) | Defense expected to move at close of plaintiff's case |
| Post-trial | OpenAI Counterclaims — Phase 2 Trial | Pending Phase 1 outcome |
| Post-trial | Antitrust Claims Phase 2 Trial | Counts VIII-XV stayed |

---

## 13. CONTROLLING / KEY CASELAW

### 13.1 Charitable Trust Creation (Plaintiff)

**Holt v. College of Osteopathic Physicians**, 61 Cal.2d 750 (1964)
- A charitable trust can be created without a formal writing if there are consistent representations of charitable purpose and the trustee accepts the trust res.
- Quote: "A charitable trust may be created by any manifestation of intent to devote property to charitable purposes"
- Cited in Schizer expert report

**Lynch v. Spilman**, 67 Cal.2d 251 (1967)
- A donor who makes contributions based on representations of charitable purpose has standing to seek enforcement of the charitable trust.
- Cited SAC ¶¶ 200-210

**Cal. Corp. Code §5142(a)**
- The Attorney General, or "any person granted relator status," may bring action to enforce charitable trusts.
- Musk claims donor relator status
- Cited Count XVIII; Pretrial Order No. 3

**Wolf v. CIR**, 4 T.C. 752 (1945)
- Gifts with conditions attached are enforceable as charitable trusts regardless of whether formal trust documents were executed.
- Cited in Schizer report

### 13.2 Aiding & Abetting (Plaintiff)

**In re Pacific Enterprises Securities Litigation**, 47 F.3d 373 (9th Cir. 1995)
- Aiding and abetting liability requires: (1) knowledge of the primary wrong; (2) substantial assistance in the wrong; (3) causal connection.
- Cited Count XIX; MSJ Order (Dkt.390)

**Casey v. U.S. Bank Nat'l Assoc.**, 127 Cal.App.4th 1138 (2005)
- Bank held liable for aiding and abetting breach of fiduciary duty where it had actual or constructive knowledge that a wrongful transaction was being facilitated.
- Cited Count XIX; PX230 (Kevin Scott email)

### 13.3 Statute of Limitations (Defense)

**California Code of Civil Procedure §338**
- 3-year limitations period for fraud and breach of fiduciary duty claims
- Accrual: when plaintiff discovers or should have discovered the wrong
- Cited Pretrial Order No. 3 (Dkt.456)

**California Code of Civil Procedure §343**
- Residual 4-year statute of limitations applies to unjust enrichment claims not covered by specific provisions
- Cited Pretrial Order No. 3

### 13.4 Charitable Solicitations (Both Sides — Live Trial Issue)

**People ex rel. Becerra v. Johnson & Johnson**, 77 Cal.App.5th 295 (2022)
- Cal. Bus. & Prof. Code §17510.8 applies to solicitations of charitable contributions regardless of whether entity is a formal charitable trust.
- Cited Trial Brief Dkt.515; §17510.8 dispute live at trial

### 13.5 Fiduciary Duties (Plaintiff)

**Donovan v. Bierwirth**, 680 F.2d 263 (2d Cir. 1982)
- Trustees of charitable entities must act exclusively in the interest of the beneficiaries; self-dealing violates fundamental fiduciary obligations.
- Cited Count XVIII theories

### 13.6 Defenses

**Cleveland v. Policy Management Systems**, 526 U.S. 795 (1999)
- The equitable doctrine of unclean hands requires that defendant's alleged wrongdoing be sufficiently related to plaintiff's misconduct.
- Quote: "The doctrine applies only where the party's misconduct is directly related to the claim being asserted"
- Cited Defense — unclean hands challenge

### 13.7 Trust Res / For-Profit Structure

**OpenAI LP Agreement (October 2018)** — PX200 / DX-839
- Document itself states investors should "view investment…in the spirit of a donation"
- Plaintiff argues this language confirms charitable trust obligations continued
- Quote: "view any investment in OpenAI in the spirit of a donation"
- Cited Count XVIII

---

## 14. ATTORNEYS

### 14.1 Plaintiff Counsel

| Attorney | Firm | Role | Email | Phone | Notable Dkts |
|---|---|---|---|---|---|
| Marc Toberoff | Toberoff & Associates, P.C. | Lead Counsel (PHV) | mtoberoff@toberoffandassociates.com | (310) 246-3333 | 1, 46, 127, 350 |
| Steven F. Molo | MoloLamken LLP | Lead Trial Counsel (PHV) | smolo@mololamken.com | (212) 607-8160 | 350, 427 |
| Robert K. Kry | MoloLamken LLP | Trial Counsel (PHV) | rkry@mololamken.com | — | 128, 350 |
| Jennifer M. Schubert | MoloLamken LLP | Trial Counsel (PHV) | jschubert@mololamken.com | — | 396 |
| Walter H. Hawes IV | MoloLamken LLP | Trial Counsel (PHV) | whawes@mololamken.com | — | 234, 285 |

- **Marc Toberoff** — Bar: CA #188547. Former entertainment law specialist turned litigation partner. Argued PI motion; led briefing strategy.
- **Steven F. Molo** — Bar: NY. Lead trial attorney; former AUSA SDNY. Handling direct examination of key witnesses including Musk.
- **Robert K. Kry** — Bar: NY. Briefing lead on MTDs, MSJ opposition. Trial preparation and jury instructions.
- **Jennifer M. Schubert** — Bar: NY. Expert witness coordination. Wazzan and Schizer reports. MIL briefing.
- **Walter H. Hawes IV** — Bar: DC. Discovery coordination. Sutskever deposition disputes. ESI matters.

### 14.2 OpenAI Defense Counsel

| Attorney | Firm | Role | Email | Phone | Notable Dkts |
|---|---|---|---|---|---|
| Jordan Eth | Morrison & Foerster LLP | Lead Local Counsel | jeth@mofo.com | (415) 268-7126 | 13, 103, 175, 327 |
| William Savitt | Wachtell, Lipton, Rosen & Katz | Lead PHV Trial Counsel | wdsavitt@wlrk.com | (212) 403-1000 | 327, 359 |
| Bradley R. Wilson | Wachtell, Lipton, Rosen & Katz | PHV Trial Counsel | brwilson@wlrk.com | — | 327, 357, 359 |
| Randall W. Jackson | Wachtell, Lipton, Rosen & Katz | PHV Trial Counsel | rwjackson@wlrk.com | — | — |
| Alison Lynn Plessman | Hueston Hennigan LLP | Local Counsel | aplessman@hueston.com | (213) 788-4340 | — |

- **Jordan Eth** — Bar: CA #121617. Lead local counsel; filed first appearances; coordinating with Wachtell at trial.
- **William Savitt** — Bar: NY. Lead trial counsel for OpenAI; Wachtell's top litigator; cross-examination of Musk; direct of Altman.
- **Bradley R. Wilson** — Bar: NY. Trial briefing; pretrial orders; MSJ brief lead.
- **Randall W. Jackson** — Bar: NY. Trial witness coordination; deposition prep.
- **Alison Lynn Plessman** — Bar: CA. Local counsel for court filings; discovery coordination.

### 14.3 Microsoft Defense Counsel

| Attorney | Firm | Role | Email | Phone | Notable Dkts |
|---|---|---|---|---|---|
| Russell P. Cohen | Dechert LLP | Lead Counsel | russ.cohen@dechert.com | (415) 262-4500 | 102, 173, 329, 357 |
| Andrew J. Levander | Dechert LLP | PHV Trial Counsel | andrew.levander@dechert.com | (212) 698-3683 | 102, 329 |
| John A. Jurata Jr. | Dechert LLP | PHV Trial Counsel | jay.jurata@dechert.com | — | 329, 357 |
| Howard M. Ullman | Dechert LLP | Trial Counsel | howard.ullman@dechert.com | — | — |

- **Russell P. Cohen** — Bar: CA #213104. Lead local + PHV counsel for Microsoft; Filed all Microsoft motions; Handling Nadella/Scott direct examinations.
- **Andrew J. Levander** — Bar: NY. Dechert's senior partner leading Microsoft defense; Strategy counsel.
- **John A. Jurata Jr.** — Bar: DC. Microsoft MSJ lead; MIL briefing; damages arguments.
- **Howard M. Ullman** — Bar: CA. Local support for Microsoft discovery and trial preparation.

---

## 15. KEY LOCATIONS

| Entity | Location | Notes |
|---|---|---|
| **N.D. Cal. — Judge YGR** | Courtroom 1, 4th Floor, 1301 Clay Street, Oakland CA 94612 | Trial in progress. Magistrate Judge Hixson handles discovery. Clerk: Frances Stone, ygr_crd@cand.uscourts.gov |
| **OpenAI Headquarters** | 3180 18th Street, San Francisco, CA 94110 | Primary operational location; site of board meetings including Nov 2023 firing/reinstatement of Altman |
| **Microsoft Corporation HQ** | One Microsoft Way, Redmond, WA 98052 | Washington corporation. Kevin Scott and Satya Nadella principal witnesses |
| **xAI Corp.** | Palo Alto, CA | Musk's competing AI company; formed March 2023 |
| **Tesla, Inc.** | Austin, TX (HQ); formerly Palo Alto | 2017 Tesla merger proposal relevant to unclean hands |
| **Safe Superintelligence Inc. (Sutskever)** | Palo Alto, CA | Sutskever's company after leaving OpenAI |

---

## 16. LOCAL RULES & STANDING ORDERS

### 16.1 Judge YGR Individual Standing Order (Courtroom 1, Oakland)
- **Pre-hearing:** Submit 3-page letter brief at least 7 days before any noticed hearing (both parties; no separate reply unless ordered)
- **Page limits:** Strictly enforced — motions 25 pp, oppositions 25 pp, replies 15 pp; sur-replies require leave
- **Trial:**
  - Counsel must pre-mark all exhibits before trial
  - PX (plaintiff), DX (defense), JX (joint), MDX (demonstratives)
  - Exhibit list filed 7 days before trial
- **Witnesses:** Direct examination by declaration for bench trials unless YGR orders otherwise
- **Jurors:** No social media research on jurors during trial; questionnaires approved in advance
- **Discovery:** All disputes to Magistrate Judge via 5-page joint letter — do NOT file discovery motions with YGR
- **Chambers copies:** Required for any document over 10 pages filed within 24 hours of ECF filing; to Clerk, Rm 400, 1301 Clay St Oakland

### 16.2 Trial Procedures (Courtroom 1)
- **Daily schedule:**
  - 8:30 AM counsel setup
  - 9:00 AM jury/bench session
  - 1-hour lunch recess typically 12:00-1:00 PM
  - 4:30 PM adjourn unless otherwise ordered
- **Objections:** Standing objections not permitted — must object each time
- **Bench conferences:** Counsel must approach only when invited
- **Technology:** IT coordinator must be approved 72 hours in advance; laptop/HDMI connection available; Elmo document camera in courtroom
- **Sequestration:** Witnesses sequestered; not to read trial transcripts during trial
- **Post-trial juror contact:** YGR does not allow counsel to contact jurors after discharge without court order

### 16.3 N.D. Cal. Civil Local Rules (key for this case)
- **L.R. 7-1 to 7-12** — Motions: opposition due 14 days; reply 7 days; no oral argument unless ordered
- **L.R. 7-3** — Meet-and-confer required before any motion (except TRO)
- **L.R. 11-3** — Signature block with bar number and firm
- **L.R. 16-9** — Pretrial conference statement 21 days before trial
- **L.R. 37-1** — Discovery disputes: confer within 7 days; then 5-page joint letter to Magistrate
- **L.R. 54-1** — Bill of costs within 14 days of judgment
- **L.R. 65-1** — TRO and PI: must file proposed order
- **Filing:** ECF mandatory; chambers copy required for filings over 25 pages

### 16.4 ESI Guidelines
- **Production format:** Native with extracted text and metadata; TIFF with OCR for redactions
- **Metadata fields:** Custodian, date created, date modified, file type, MD5 hash
- **Privilege logs:** Within 30 days of assertion; Sedona Conference format preferred
- **FRCP 502(d) clawback:** Required protective order
- **30(b)(6) notice:** At least 21 days before deposition
- **Deposition exhibits:** Pre-marked and served 48 hours in advance
- **Model Protective Order:** Two-tier (Confidential / Attorneys Eyes Only)

---

## 17. CRITICAL FACTS / FIGURES (Quick Reference)

| Fact | Value | Source |
|---|---|---|
| Case Number | 4:24-cv-04722-YGR | — |
| Court | N.D. Cal. (Oakland) | — |
| Judge | Yvonne Gonzalez Rogers | — |
| Magistrate | Thomas Hixson (since 6/27/2025) | Dkt.187 |
| Original suit filed (state court) | February 2024 | — |
| Federal complaint filed | August 5, 2024 | Dkt.1 |
| FAC filed | November 14, 2024 | Dkt.32 |
| SAC filed | May 22, 2025 | Dkt.170 |
| PI denied | March 4, 2025 | Dkt.121 |
| MSJ hearing | January 7, 2026 | Dkt.383 |
| MSJ Order | January 15, 2026 | Dkt.390 |
| Jury selection | April 27, 2026 | Dkt.506 |
| Trial begins | April 28, 2026 | Dkt.510 |
| Estimated trial end | ~May 22, 2026 | Dkt.386 |
| OpenAI founding | October 2015 | — |
| OpenAI 501(c)(3) status | IRS approved | — |
| **Musk total donations** | **~$38 million (~$38.2M tracked)** | PX112/DX-1304 |
| OpenAI LP formation | October 2018 | PX200 |
| Microsoft initial investment | $1 billion (July 2019) | — |
| **Microsoft total investment** | **$13.75B+** | — |
| **Microsoft equity value** | **~$135B+** | PX286/287 |
| OpenAI valuation | ~$300B+ | — |
| OpenAI PBC conversion | October 2025 | — |
| Microsoft equity stake post-PBC | ~27% | PX286/287 |
| Total exhibits on Joint Exhibit List | 1,601 | Dkt.428 |
| Exhibits admitted (as of Day 3 4/30/26) | 23 | War room data |
| Pivotal exhibits in | PX-003, PX-163, PX-230, DX-516 | — |
| Pivotal exhibits queued Day 3 PM | PX-200 (LP Agreement) | — |
| Trial Day 1 duration | 4:59 hours | Dkt.510 |
| Trial Day 2 duration | 5:54 hours | Dkt.513 |
| Trial Day 3 duration | 5:32 hours | Dkt.521 |
| Plaintiff's MILs | 5 (Dkts.402-407) | — |
| OpenAI MILs | 2 (Dkts.409-410) | — |
| Microsoft MILs | 3 (Dkts.412-415) | — |
| Wazzan damages opinions surviving Daubert | 3-6 (1-2 excluded) | Dkt.452 |
| Pretrial Orders | PTO 1 (Dkt.446); PTO 2 (Dkt.452); PTO 3 (Dkt.456); PTO 4 (Dkt.477) | — |

---

## 18. SETTLEMENT POSTURE (PRE-TRIAL)

### 18.1 Settlement Ledger (as of trial)
| Date | Type | Side | Note |
|---|---|---|---|
| Feb 2024 | Original suit filed (San Francisco state) | — | Voluntarily dismissed |
| Apr 2024 | Refiled in N.D. Cal. as Musk v. Altman et al | — | Reassigned to Judge YGR |
| Aug 2024 | Initial framework demand: structural restructuring + return to non-profit | Plaintiff | Equitable demand · no monetary anchor (Dkt.45 letter brief) |
| Mar 4, 2025 | PI DENIED (YGR) — Defense-favorable equity signal | Court | Settlement leverage shifted to OpenAI |
| Oct 8, 2025 | Phase 1 trial set; two-phase order entered | Court | Phase 2 antitrust stayed |
| Apr 22, 2026 | Trial begins | Court | Settlement discussions paused during trial |

### 18.2 BATNA Analysis
- **Plaintiff trial cost:** ~$15M (Phase 1 + appeals)
- **Plaintiff probability-weighted outcome:** ~$8B (equitable remedies, constructive trust on for-profit equity)
- **Plaintiff floor:** ~$500M (minimum acceptable structural remedy)
- **Defendant trial cost:** ~$25M (Phase 1 + Phase 2 antitrust counterclaims)
- **Defendant risk assessment:** ~$15B (equity disgorgement worst case)
- **Defendant ceiling:** ~$2B (acceptable monetary substitution)
- **Two-phase structure:** If Phase 1 plaintiff verdict, Phase 2 antitrust counterclaims trigger immediately. Cascading liability creates non-linear exposure.
- **Settlement zone:** Theoretical pre-verdict. Phase 1 verdict will create immediate sharp settlement leverage. Pre-verdict structural-reform settlement (charitable foundation governance + research-mission preservation) is likely the only path that doesn't require monetary disgorgement at OpenAI-fatal scale.
- **Microsoft separation:** Microsoft's separate aiding-and-abetting exposure (Count XIX) is the partition where MS may settle independently regardless of OpenAI position.

### 18.3 Comparable Cases
- **NCAA v. House (NIL settlement)** — N.D. Cal. (Wilken), 2024, $2.78B antitrust class settlement with structural injunctive relief. Hybrid charitable-structural remedy framework.
- **CA Charitable Trust Enforcement Pattern (CA AG actions)** — Various Cal. Sup. Ct., 2020. Cy pres restoration framework — most directly applicable remedy structure. Suggests Phase 1 plaintiff verdict supports settlement preserving research mission with structural reforms.
- **Donor Restitution Cases under Cal. §17510.8** — CA state actions, 2019, $25M. Directly relevant to pending YGR ruling on statute applicability.
- **CA Attorney General declined to intervene** — 2025. Strongest defense leverage point — state authority charged with charitable trust enforcement reviewed and declined.
- **SEC v. Theranos / Holmes** — N.D. Cal., 2018. Civil penalty + officer bar. Misrepresentation analogy.

---

## 19. CASE NARRATIVE — PLAINTIFF FRAMING

**Chapter 1 — The Founding Vision** In May 2015, Sam Altman and Greg Brockman wrote to Elon Musk and a small circle of co-founders. They were starting a non-profit AI lab. The mission, in Altman's own words: "Tech belongs to the world." Musk pledged tens of millions of dollars. So did others. The IRS would ultimately grant 501(c)(3) status. OpenAI, Inc. was a charitable trust — funded by donors who believed the technology would never be enclosed.

**Chapter 2 — Brockman's Doubt** By 2017 the founders were already arguing about whether the non-profit could ever raise enough capital to compete with Google. In a contemporaneous journal entry — pre-litigation, written by a co-founder — Greg Brockman acknowledged that the LP structure under consideration was "wrong to steal non-profit from Elon." The journal is plaintiff's exhibit PX-163. It is not litigation posturing. It is a confession.

**Chapter 3 — The Pivot** In March 2019 OpenAI announced its for-profit subsidiary structure. Months later, Microsoft invested $1 billion. By 2024 it had invested more than $13 billion. The non-profit's most valuable asset — the technology paid for by charitable donations — was now controlled by a for-profit entity. Microsoft eventually held an equity position worth more than $135 billion. Charity money had funded the most valuable AI company in history.

**Chapter 4 — The Conversion** In October 2025 OpenAI converted to a public benefit corporation. The non-profit became a minority stakeholder of the entity it had created. The board that approved the conversion was the same board that had taken the donations. The plaintiff's theory: that conversion is a per-se breach of the charitable trust, and the constructive remedy is disgorgement of the for-profit equity.

**Chapter 5 — The Theory** Plaintiff's case rests on three counts. Count XVIII — breach of charitable trust. Count IV — unjust enrichment. Count XIX — aiding and abetting against Microsoft. The factual proofs are concrete: Altman's PX-003 founding email, Brockman's PX-163 journal, and Kevin Scott's PX-230 internal email at Microsoft sixteen months before the $1 billion investment. The remedy sought is constructive trust on the for-profit equity.

**Chapter 6 — What's at Stake** This is not a typical contract case. It is a charitable-trust enforcement action with constructive-trust remedies — a body of law where equity protects the donor class against fiduciary self-dealing. Judge Yvonne Gonzalez Rogers is hearing Phase 1 now. The verdict will turn on whether Altman, Brockman, and Microsoft executives told the truth about what OpenAI was, or what they wanted it to be.

---

## 20. CASE NARRATIVE — DEFENSE FRAMING

**Chapter 1 — The Mission Was Always Practical** OpenAI was founded to build safe artificial general intelligence. The non-profit form was a vehicle, not a vow. The May 2015 founding email used aspirational language because all founding documents do. What followed was a decade of disciplined research, peer-reviewed publication, and — when capital requirements made the non-profit form impossible to maintain — a careful restructuring that preserved the mission inside a capped-profit entity.

**Chapter 2 — Musk Was Always Informed** In August 2018 Musk received the term sheet for the LP structure. He said nothing for months. In January 2016 he had already replied to a proposal that OpenAI become "less open" with a single word: "Yup." That email is defense exhibit DX-516. In March 2019, Altman asked him to acknowledge the for-profit structure publicly. His instruction came back: "Tell them I'm supportive." That is DX-862. The record is consistent. Musk knew, and Musk acquiesced.

**Chapter 3 — No Donor Standing** California charitable-trust enforcement is statutorily vested in the Attorney General — not in individual donors. Musk is not a charitable actor. He is a competitor. He runs xAI. The Attorney General reviewed Musk's allegations and declined to join his suit. That declination is itself a judgment by the state authority charged with protecting California's charitable assets.

**Chapter 4 — Laches Bars Equity** Musk knew of OpenAI's for-profit structure by March 2019. In September 2020 he tweeted that OpenAI had been "captured by Microsoft." He filed suit five years after that. During those five years, OpenAI invested billions of dollars in reliance on the structure Musk now seeks to unwind. Equity does not rescue a plaintiff who slept on his rights while watching the value he claims accrue to others.

**Chapter 5 — Microsoft Did Its Own Diligence** On Count XIX, plaintiff alleges Microsoft aided and abetted a charitable-trust breach. But Microsoft conducted its own due diligence on the LP structure for fifteen months before the $1 billion investment. PX-230 — Kevin Scott's internal email — shows MS asking the right questions, not sleepwalking through them. To find aiding and abetting, the jury must find that Microsoft knowingly assisted a breach. The record shows Microsoft acted in good faith.

**Chapter 6 — The Phases Ahead** After the Phase 1 verdict, Phase 2 antitrust counterclaims will activate. OpenAI alleges that Musk used this litigation as a competitive weapon — to slow OpenAI down while xAI catches up. Pretrial Order No. 3 set the SOL framework. Defense exhibits DX-516, DX-827, and DX-862 will be introduced systematically. The defense theory is not just that Musk is wrong on the law. It is that he is in court to win a market, not a charity.

---

## 21. CASE NARRATIVE — JUDGE YGR PERSPECTIVE

**Chapter 1 — The Phases** Judge YGR structured this trial in two phases. Phase 1 covers Count XVIII — charitable trust breach — Count IV — unjust enrichment — and Count XIX — aiding and abetting Microsoft. Phase 2, the antitrust counterclaims, was stayed pending Phase 1 verdict. The structure forces clarity: equity claims first, market claims second.

**Chapter 2 — The Preliminary Injunction Denial** In March 2025, Judge YGR denied Musk's preliminary injunction. Her order found no irreparable harm from the for-profit conversion — a ruling that telegraphed her view of the equities. The PI denial did not resolve the merits, but it set the tone. Plaintiff would need to prove charitable-trust breach at trial. Status quo would not be enjoined while that happened.

**Chapter 3 — Pretrial Order No. 3** Pretrial Order No. 3 set the statute-of-limitations framework. The order references Musk's September 2020 "captured by Microsoft" tweet as a potential trigger date. That single tweet now drives the SOL question. Plaintiff must prove fraudulent concealment to toll. The order is consequential — a structural framing that organizes how every fact is heard.

**Chapter 4 — The §17510.8 Question** On April 30, 2026, both sides filed competing briefs on whether California Business and Professions Code §17510.8 — the charitable-solicitations strict-liability statute — applies. If it applies, the standard for liability shifts. If it does not, the case is decided on common-law charitable-trust principles. The Court has not yet ruled. The ruling, when it issues, will be the most consequential evidentiary decision of the trial.

**Chapter 5 — YGR's Style** Judge YGR runs a tight courtroom. She has read every filing. She identifies inconsistencies in real time. She presses witnesses personally. Counsel must know the record cold — every exhibit, every transcript line, every cross-reference. Overreach is punished. Vague statements draw immediate questions. The trial moves fast because the Judge moves fast.

**Chapter 6 — What Comes Next** Phase 1 verdict will be the next major event. Whatever the result, post-trial motions will follow — Rule 59 motions, fee motions, possible appeals. If plaintiff prevails, Phase 2 antitrust counterclaims activate immediately. If defense prevails, OpenAI may still pursue counterclaims to recover litigation costs.

---

## 22. ANSWERING GUIDANCE

When this document is fed to a model:

1. **For factual questions** (dates, names, dollar amounts, docket numbers, filings): cite the specific section and reference number above. Do not infer or estimate.
2. **For procedural questions:** walk through Section 5 (Procedural Timeline) and Section 6 (Docket Entries).
3. **For trial-day questions** (current witness, exhibits in/queued, live developments): use Section 3.7 (Live war room) and Section 6 (recent docket).
4. **For substantive theory questions:** use Sections 8 (plaintiff) and 9 (defense).
5. **For evidence questions:** use Section 10 (PX/DX/MDX exhibit catalog).
6. **For witness questions:** use Section 11 (deposition citations and key admissions).
7. **For caselaw questions:** use Section 13.
8. **For settlement questions:** use Section 18.
9. **For unknown facts not in this document:** respond *"I don't have that information in the case reference materials."* Do not guess.
10. **As of May 7, 2026:** trial is in progress (Day 4 expected); estimated end ~May 22, 2026. Microsoft Phase 1 in progress; Phase 2 antitrust counterclaims stayed.

### Key open issues at trial
- **§17510.8 applicability** — competing trial briefs filed Dkt.515 (plaintiff) / Dkt.516 (OpenAI) on April 30, 2026; YGR ruling pending
- **Brockman threat admissibility** — OpenAI Dkt.522 application pending; FRE 408(b) basis
- **Sutskever Vol. 2 deposition** — Court-ordered (Dkt.324); designations not yet finalized
- **Phase 1 verdict** — Will trigger Phase 2 antitrust counterclaims if plaintiff prevails

---

*End of Master Reference Document*
*Source: Compiled from 4:24-cv-04722-YGR docket and case file as of May 7, 2026 (Trial in progress)*
`;
